import { useState, useEffect, useCallback, useRef } from "react";
import { Card } from "../ui/Card";
import { Input } from "../ui/Input";
import { Button } from "../ui/Button";
import { X, Search, User, Loader2, Users as UsersIcon } from "lucide-react";
import { userService } from "../../services/userService";

interface StudentSelectorProps {
  selectedUserIds: string[];
  onChange: (userIds: string[]) => void;
  className?: string;
}

interface User {
  _id: string;
  firstName?: string;
  lastName?: string;
  email: string;
  role: string;
  profilePicture?: string;
}

export function StudentSelector({
  selectedUserIds,
  onChange,
  className = "",
}: StudentSelectorProps) {
  const [search, setSearch] = useState("");
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [allSelected, setAllSelected] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const loadUsers = useCallback(
    async (searchTerm = "", loadingStateSetter = setIsLoading) => {
      loadingStateSetter(true);
      try {
        const response = await userService.getAllUsers({
          search: searchTerm || undefined,
        });

        const newUsers = response.users || [];
        setUsers(newUsers);
      } catch (error) {
        console.error("Failed to load users:", error);
      } finally {
        loadingStateSetter(false);
      }
    },
    [],
  );

  useEffect(() => {
    loadUsers("");
  }, [loadUsers]);

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      loadUsers(search, setSearchLoading);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [search, loadUsers]);

  const handleToggleUser = (userId: string) => {
    if (selectedUserIds.includes(userId)) {
      onChange(selectedUserIds.filter((id) => id !== userId));
      if (allSelected) setAllSelected(false);
    } else {
      onChange([...selectedUserIds, userId]);
    }
  };

  const handleToggleAll = () => {
    if (allSelected) {
      onChange([]);
      setAllSelected(false);
    } else {
      const allIds = users.map((u) => u._id);
      onChange([...new Set([...selectedUserIds, ...allIds])]);
      setAllSelected(true);
    }
  };

  const handleRemoveUser = (userId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(selectedUserIds.filter((id) => id !== userId));
    if (allSelected) setAllSelected(false);
  };

  const handleClearAll = () => {
    onChange([]);
    setAllSelected(false);
  };

  const selectedUsers = users.filter((u) => selectedUserIds.includes(u._id));

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="relative">
        <Search
          size={16}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
        />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or email..."
          className="pl-10"
        />
        {searchLoading && (
          <Loader2
            size={16}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 animate-spin"
          />
        )}
      </div>

      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={allSelected}
            onChange={handleToggleAll}
            className="w-4 h-4 text-[#f09040] border-gray-300 rounded focus:ring-[#f09040]"
          />
          <span className="text-sm font-medium text-[#102040]">
            {allSelected ? "All selected" : "Select all"}
          </span>
        </label>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClearAll}
          disabled={selectedUserIds.length === 0}
          className="text-sm text-[#989797] hover:text-[#102040]"
        >
          Clear all
        </Button>
      </div>

      {selectedUserIds.length > 0 && (
        <Card className="p-3 bg-[#FFF9E6] border-[#f09040]">
          <div className="flex items-center gap-2 mb-2">
            <UsersIcon size={16} className="text-[#e07828]" />
            <span className="text-sm font-medium text-[#102040]">
              {selectedUserIds.length} selected
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {selectedUsers.map((user) => (
              <div
                key={user._id}
                className="inline-flex items-center gap-1 px-2 py-1 bg-white rounded-full border border-[#e07828]"
              >
                <span className="text-xs text-[#102040]">
                  {user.firstName || user.lastName
                    ? `${user.firstName || ""} ${user.lastName || ""}`.trim()
                    : user.email}
                </span>
                <button
                  onClick={(e) => handleRemoveUser(user._id, e)}
                  className="hover:bg-gray-100 rounded-full p-0.5"
                >
                  <X size={12} className="text-gray-500" />
                </button>
              </div>
            ))}
            {selectedUserIds.length > selectedUsers.length && (
              <span className="text-xs text-[#989797]">
                +{selectedUserIds.length - selectedUsers.length} more
              </span>
            )}
          </div>
        </Card>
      )}

      <Card className="max-h-64 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 size={24} className="text-[#f09040] animate-spin" />
          </div>
        ) : users.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <User size={40} className="text-gray-300 mb-2" />
            <p className="text-sm text-[#989797]">
              {search ? "No users found" : "No users available"}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {users.map((user) => (
              <label
                key={user._id}
                className="flex items-start gap-3 p-3 hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <div className="pt-0.5">
                  <input
                    type="checkbox"
                    checked={selectedUserIds.includes(user._id)}
                    onChange={() => handleToggleUser(user._id)}
                    className="w-4 h-4 text-[#f09040] border-gray-300 rounded focus:ring-[#f09040]"
                  />
                </div>
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  {user.profilePicture ? (
                    <img
                      src={user.profilePicture}
                      alt=""
                      className="w-8 h-8 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-[#e07828] flex items-center justify-center text-white text-sm font-medium">
                      {(user.firstName || user.lastName || user.email)
                        .charAt(0)
                        .toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#102040] truncate">
                      {user.firstName || user.lastName
                        ? `${user.firstName || ""} ${user.lastName || ""}`.trim()
                        : user.email}
                    </p>
                    <p className="text-xs text-[#989797] truncate">
                      {user.email}
                    </p>
                    <p className="text-xs text-[#e07828] capitalize mt-0.5">
                      {user.role}
                    </p>
                  </div>
                </div>
              </label>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
